import {
  Box,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DesignerFileImageDisplay } from "../file-image-display";
import AddIcon from "@mui/icons-material/Add";
import { LocalProjectFileImage } from "./add-project-container";
import { useCallback, useMemo } from "react";

type ProjectImageListProps = {
  images: LocalProjectFileImage[];
  onReorder: (ordered: LocalProjectFileImage[]) => void;
  onDelete: (index: number) => void;
  onSetCover: (index: number) => void;
  onEditDetails?: (index: number) => void;
};

export const ProjectImageList = ({
  images,
  onReorder,
  onDelete,
  onSetCover,
  onEditDetails,
}: ProjectImageListProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const imageIds = images.map((image) => image._id || image.local_url);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = imageIds.indexOf(String(active.id));
      const newIndex = imageIds.indexOf(String(over.id));
      if (oldIndex === -1 || newIndex === -1) return;
      const reordered = arrayMove(images, oldIndex, newIndex);
      onReorder(reordered);
    },
    [imageIds, images, onReorder]
  );

  const handleMobileReorder = useCallback(
    (index: number, position: number) => {
      if (position < 0 || position >= images.length) return;
      const updated = [...images];
      const [moved] = updated.splice(index, 1);
      updated.splice(position, 0, moved);
      onReorder(updated);
    },
    [images, onReorder]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } })
  );

  if (isMobile) {
    return (
      <Grid container spacing={2}>
        {images.map((image, index) => (
          <Grid item xs={6} key={image._id || image.local_url}>
            <Box display="flex" flexDirection="column" gap={1.5}>
              <DesignerFileImageDisplay
                src={image.local_url}
                isCover={index === 0}
                onDelete={() => Promise.resolve(onDelete(index))}
                onSetCover={index === 0 ? undefined : () => onSetCover(index)}
                onEditDetails={onEditDetails ? () => onEditDetails(index) : undefined}
                sx={{ height: 150, borderRadius: "8px" }}
              />
              {image.title && (
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "11px",
                    color: "text.secondary",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    lineHeight: 1.3,
                  }}
                  title={image.title}
                >
                  {image.title}
                </Typography>
              )}
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Position
                </Typography>
                <Select
                  size="small"
                  value={String(index)}
                  onChange={(event: SelectChangeEvent<string>) =>
                    handleMobileReorder(index, Number(event.target.value))
                  }
                  fullWidth
                >
                  {images.map((_, position) => (
                    <MenuItem key={position} value={String(position)}>
                      {position + 1}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </Box>
          </Grid>
        ))}
        {images.length === 0 && <Grid item xs={6} />}
      </Grid>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={imageIds} strategy={verticalListSortingStrategy}>
        <Grid container spacing={2}>
          {images.map((image, index) => (
            <SortableImageCard
              key={image._id || image.local_url}
              id={image._id || image.local_url}
              index={index}
              image={image}
              onDelete={onDelete}
              onSetCover={onSetCover}
              onEditDetails={onEditDetails}
            />
          ))}
          {images.length === 0 && <Grid item xs={12} sm={6} md={4} lg={3} />}
        </Grid>
      </SortableContext>
    </DndContext>
  );
};

type SortableImageCardProps = {
  id: string;
  index: number;
  image: LocalProjectFileImage;
  onDelete: (index: number) => void;
  onSetCover: (index: number) => void;
  onEditDetails?: (index: number) => void;
};

const SortableImageCard = ({
  id,
  index,
  image,
  onDelete,
  onSetCover,
  onEditDetails,
}: SortableImageCardProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    cursor: "grab",
  } as React.CSSProperties;

  return (
    <Grid item xs={12} sm={6} md={4} lg={3} ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <Box display="flex" flexDirection="column" gap={0.5}>
        <DesignerFileImageDisplay
          src={image.local_url}
          isCover={index === 0}
          onDelete={() => Promise.resolve(onDelete(index))}
          onSetCover={index === 0 ? undefined : () => onSetCover(index)}
          onEditDetails={onEditDetails ? () => onEditDetails(index) : undefined}
          sx={{ height: 150, borderRadius: "8px" }}
        />
        {image.title && (
          <Typography
            variant="caption"
            sx={{
              fontSize: "11px",
              color: "text.secondary",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              lineHeight: 1.3,
              px: 0.5,
            }}
            title={image.title}
          >
            {image.title}
          </Typography>
        )}
      </Box>
    </Grid>
  );
};

const EmptySlotCard = () => (
  <Box
    sx={{
      height: 150,
      border: "1px dashed #D9D9D9",
      borderRadius: "8px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      color: "#9E9E9E",
      backgroundColor: "#FBFBFB",
      gap: 1,
    }}
  >
    <AddIcon fontSize="small" />
    <Typography variant="body2" fontWeight={600}>
      Add
    </Typography>
  </Box>
);
